/**
 * Client-side image cache.
 *
 * Why this exists: the ingress (Cloudflare) forces `Cache-Control: no-store`
 * on every `/api/*` response, so auth-gated images served from
 * `/api/v2/storage/serve` (composed card heroes, plates, asset thumbnails)
 * are re-downloaded from scratch on every navigation — causing the slow
 * "blank then reload" the user reported. We can't change the ingress, so we
 * cache the bytes ourselves: the first load fetches + stores an object URL
 * keyed by the (stable) image URL; subsequent loads of the same URL are
 * instant with zero network. The key changes only when the image changes
 * (e.g. a recompose produces a new file), so the cache refreshes exactly
 * when — and only when — the user changes the image.
 *
 * External / public URLs (Pexels, Unsplash, data:, blob:) are returned
 * untouched — the browser caches those normally and cross-origin fetch+blob
 * would need CORS.
 */

const MAX_ENTRIES = 250; // simple LRU cap to bound memory
const cache = new Map(); // url -> { objectUrl?: string, promise?: Promise<string> }

function isApiUrl(url) {
  if (!url || typeof url !== "string") return false;
  if (url.startsWith("blob:") || url.startsWith("data:")) return false;
  if (url.startsWith("/api/")) return true;
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

function touch(url, entry) {
  // LRU: re-insert to move to most-recent, then evict oldest beyond the cap.
  cache.delete(url);
  cache.set(url, entry);
  while (cache.size > MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    const oldest = cache.get(oldestKey);
    cache.delete(oldestKey);
    if (oldest?.objectUrl) {
      try { URL.revokeObjectURL(oldest.objectUrl); } catch { /* noop */ }
    }
  }
}

/** Synchronous peek — returns the cached object URL if present, else the raw
 *  URL for external images, else null (a fetch is needed). Lets components
 *  paint instantly on a cache hit with no flash. */
export function peekCachedImageUrl(url) {
  if (!isApiUrl(url)) return url || null;
  const entry = cache.get(url);
  return entry?.objectUrl || null;
}

/** Resolve an image URL through the cache. Always resolves to a usable URL;
 *  on fetch failure it falls back to the raw URL so the <img> can still try. */
export async function getCachedImageUrl(url) {
  if (!isApiUrl(url)) return url;

  const existing = cache.get(url);
  if (existing) {
    touch(url, existing);
    if (existing.objectUrl) return existing.objectUrl;
    if (existing.promise) return existing.promise;
  }

  const promise = (async () => {
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) throw new Error(`image fetch ${r.status}`);
    const blob = await r.blob();
    const objectUrl = URL.createObjectURL(blob);
    touch(url, { objectUrl });
    return objectUrl;
  })();

  cache.set(url, { promise });
  try {
    return await promise;
  } catch {
    cache.delete(url);
    return url; // fall back to the raw URL
  }
}

/** Drop a URL from the cache (revoking its object URL). */
export function invalidateCachedImage(url) {
  const entry = cache.get(url);
  if (entry?.objectUrl) {
    try { URL.revokeObjectURL(entry.objectUrl); } catch { /* noop */ }
  }
  cache.delete(url);
}
